import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { db } from '../config/firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';

export default function AdminSetup() {
  const { currentUser, loading, fetchUserRole, userRole } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [promoting, setPromoting] = useState(false);
  const [firestoreRole, setFirestoreRole] = useState(null);

  useEffect(() => {
    if (currentUser) {
      checkFirestoreRole();
    }
  }, [currentUser]);

  const checkFirestoreRole = async () => {
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const docSnap = await getDoc(userDocRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setFirestoreRole(data.currentRole || 'not set');
      } else {
        setFirestoreRole('document not found');
      }
    } catch (err) {
      console.error('Error checking Firestore:', err);
    }
  };

  const promoteToAdmin = async () => {
    try {
      setError('');
      setStatus('');
      setPromoting(true);

      const uid = currentUser.uid;
      const userDocRef = doc(db, 'users', uid);
      
      await updateDoc(userDocRef, {
        currentRole: 'master_admin',
        status: 'active',
        updatedAt: new Date(),
      });

      await new Promise(resolve => setTimeout(resolve, 1000));
      await checkFirestoreRole();
      await fetchUserRole(uid);

      setStatus('✅ Successfully promoted! Refresh dashboard to see changes.');
    } catch (err) {
      console.error('Error:', err);
      setError(`❌ Error: ${err.message}`);
    } finally {
      setPromoting(false);
    }
  };

  const forceRefresh = async () => {
    try {
      await fetchUserRole(currentUser.uid);
      await checkFirestoreRole();
      setStatus('✅ Refreshed role from Firestore');
    } catch (err) {
      setError(`❌ Error: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-5xl mb-4">🔥</div>
          <p className="text-xl">Loading...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    navigate('/signin');
    return null;
  }

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition mb-8"
        >
          <span>←</span>
          Back to Dashboard
        </button>
        
        <div className="mb-12">
          <h1 className="text-4xl font-black mb-2">🔐 Admin Setup</h1>
          <p className="text-gray-400">Promote account to master_admin</p>
        </div>

        <div className="bg-zinc-900 p-6 rounded-lg border border-zinc-800 mb-6">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <p className="text-gray-400 text-sm mb-1">Email</p>
              <p className="text-lg font-semibold">{currentUser.email}</p>
            </div>
            <div>
              <p className="text-gray-400 text-sm mb-1">App Role</p>
              <p className="text-lg font-semibold capitalize">{userRole || 'loading'}</p>
            </div>
            <div>
              <p className="text-gray-400 text-sm mb-1">Firestore Role</p>
              <p className="text-lg font-semibold capitalize text-orange-400">{firestoreRole || 'loading'}</p>
            </div>
          </div>
        </div>

        {status && (
          <div className="bg-green-500/20 border border-green-500/50 text-green-300 p-4 rounded-lg mb-6">
            {status}
          </div>
        )}

        {error && (
          <div className="bg-red-500/20 border border-red-500/50 text-red-300 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        <div className="bg-zinc-900 p-8 rounded-lg border border-zinc-800 text-center mb-6">
          <p className="text-gray-400 mb-6">
            Promote to <span className="text-orange-500 font-bold">master_admin</span>
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <button
              onClick={promoteToAdmin}
              disabled={promoting}
              className="bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 text-white px-8 py-3 rounded-lg font-bold"
            >
              {promoting ? 'Promoting...' : '🚀 Promote'}
            </button>
            <button
              onClick={forceRefresh}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-bold"
            >
              🔄 Refresh
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
